import {
  inlayCoreConfigurationSchema,
  isScanPostComplete,
  ItemScopeEnum,
  ItemTypeEnum,
  type DeepPartial,
  type InlayCoreConfiguration,
} from "@jasper/client-models";
import {
  InlayCoreCharacteristicsForm,
  validateSchema,
  type ValidationErrors,
} from "@jasper/forms";
import { useInlayCoreChoices } from "@jasper/use-cases";
import { Alert, Loader, Stack, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useStepBulkGroup } from "../hooks/useStepBulkGroup";
import { useRegisterStepSave } from "./useRegisterStepSave";

export const InlayCoreCharacteristicsStep = ({
  stepKey,
  productId,
  product,
  orderConfiguration,
  bulkGroup,
  registerStepSave,
}: ProductStepProps) => {
  const {
    isLoading: isOrderLoading,
    updateProduct,
    updateProducts,
  } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.inlayCore",
  });

  const { isBulkMode, groupProducts } = useStepBulkGroup(product, bulkGroup);

  const {
    isLoading: isChoicesLoading,
    error: choicesError,
    itemMaterials,
    scanPostCatalog,
  } = useInlayCoreChoices();

  const persistedItem = product?.items.find(
    item => item.type === ItemTypeEnum.inlayCore
  );

  const defaultValues = useMemo(
    () => ({
      configuration: {
        materialCharacteristics: persistedItem?.materialCharacteristics ?? {},
        inlayCoreCharacteristics: persistedItem?.inlayCoreCharacteristics ?? {},
      } as InlayCoreConfiguration | undefined,
    }),
    [persistedItem]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const configuration = value.configuration;

      if (!configuration) {
        return;
      }

      const buildItems = (configuredProduct: ConfigurableProduct) =>
        configuredProduct.items.map(item =>
          item.type === ItemTypeEnum.inlayCore &&
          item.scope === ItemScopeEnum.tooth
            ? { ...item, ...configuration }
            : item
        );

      try {
        if (isBulkMode) {
          await updateProducts(
            groupProducts.map(groupProduct => ({
              productId: groupProduct.id,
              items: buildItems(groupProduct),
            }))
          );
        } else {
          await updateProduct({ productId, items: buildItems(product) });
        }
      } catch {
        notifications.show({
          title: t("errorNotificationTitle", { keyPrefix: "common" }),
          message: t("errorNotificationMessage", { keyPrefix: "common" }),
          color: "red",
        });
        return;
      }
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset(defaultValues);
    }
  }, [product, defaultValues, form]);

  if (isOrderLoading || isChoicesLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">{t("productNotFound")}</Alert>
      </Stack>
    );
  }

  if (choicesError) {
    return (
      <Stack p="md">
        <Alert color="red">{t("choicesUnavailable")}</Alert>
      </Stack>
    );
  }

  const validateSelection = ({
    value,
  }: {
    value: DeepPartial<InlayCoreConfiguration> | undefined;
  }) => {
    const result = validateSchema(inlayCoreConfigurationSchema, value);
    if (!result.success) {
      return result.errors;
    }

    if (
      !isScanPostComplete(
        result.data.inlayCoreCharacteristics.scanPost,
        scanPostCatalog
      )
    ) {
      const scanPostError: ValidationErrors<InlayCoreConfiguration> = {
        inlayCoreCharacteristics: { scanPost: "custom" },
      };

      return scanPostError;
    }

    return undefined;
  };

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field
          name="configuration"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const errors = validateSelection({ value });

              if (!errors) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return errors;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <InlayCoreCharacteristicsForm
                onBlur={() => field.handleBlur()}
                value={field.state.value}
                errors={formErrors}
                onChange={next =>
                  field.setValue(next as InlayCoreConfiguration | undefined)
                }
                itemMaterials={itemMaterials}
                scanPostCatalog={scanPostCatalog}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
